import { html } from '@design.estate/dees-element';
import { demoPage, demoOutput, showDemoResult } from '../../../demos/templates.js';
import type {
  IHarnessSidebarSection,
  IHarnessSidebarSectionChangeDetail,
} from './dees-harness-sidebar.js';

// `notes` is declared without slotted content, so the panel does not offer it.
const sections: IHarnessSidebarSection[] = [
  { key: 'screencast', label: 'Screencast', icon: 'lucide:MonitorPlay', badge: '4.4 ms' },
  { key: 'transport', label: 'Transport', icon: 'lucide:Activity', badge: 'webrtc' },
  { key: 'notes', label: 'Notes', icon: 'lucide:NotebookPen' },
];

let sampleCount = 0;

/**
 * A live panel updates its rows and badges in place: the host owns the values,
 * so nothing collapses and the open section keeps its scroll position.
 */
const sampleCounters = (eventArg: Event): void => {
  const scope = (eventArg.currentTarget as HTMLElement).closest('.harnessSidebarDemo')!;
  sampleCount += 1;
  const decodeMs = (4.4 + (sampleCount % 6) * 0.3).toFixed(1);
  scope.querySelector('[data-metric="frames"]')!.textContent = `${1_440 + sampleCount * 24}`;
  scope.querySelector('[data-metric="decode"]')!.textContent = `${decodeMs} ms`;
  scope.querySelector('dees-harness-sidebar')!.sections = sections.map((section) =>
    section.key === 'screencast' ? { ...section, badge: `${decodeMs} ms` } : section,
  );
};

export const demoFunc = () => demoPage('Sidebar', 'A host-composed panel of collapsible sections in the harness sidebar style: the shell owns the accordion, every body is slotted content. One section is declared without content and is therefore not offered.', html`
  <style>
    .harnessSidebarPanel {
      width: min(100%, 300px);
      height: 420px;
      overflow: hidden;
      border: 1px solid var(--dees-color-border-subtle);
      border-radius: var(--dees-radius-xl);
    }
    .sidebarRow {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--dees-spacing-md);
      color: var(--dees-color-text-secondary);
    }
    .sidebarRow + .sidebarRow {
      margin-top: 6px;
    }
    .sidebarValue {
      color: var(--dees-color-text-primary);
      font-family: var(--dees-font-family-mono);
      font-variant-numeric: tabular-nums;
    }
  </style>
  <div class="harnessSidebarDemo dees-demo-stack">
    <div class="dees-demo-row">
      <dees-button @clicked=${sampleCounters}>Sample the counters</dees-button>
    </div>
    <div class="harnessSidebarPanel">
      <dees-harness-sidebar
        .sections=${sections}
        @harness-sidebar-section-change=${(event: CustomEvent<IHarnessSidebarSectionChangeDetail>) => {
          showDemoResult((event.currentTarget as Element).closest('.harnessSidebarDemo')!, event.detail);
        }}
      >
        <div slot="section-screencast">
          <div class="sidebarRow"><span>Codec</span><span class="sidebarValue">h264</span></div>
          <div class="sidebarRow"><span>Encoder</span><span class="sidebarValue">hardware</span></div>
          <div class="sidebarRow">
            <span>Decode</span><span class="sidebarValue" data-metric="decode">4.4 ms</span>
          </div>
          <div class="sidebarRow">
            <span>Frames</span><span class="sidebarValue" data-metric="frames">1440</span>
          </div>
          <div class="sidebarRow"><span>GPU compositing</span><span class="sidebarValue">on</span></div>
        </div>
        <div slot="section-transport">
          <div class="sidebarRow"><span>Channel</span><span class="sidebarValue">datachannel</span></div>
          <div class="sidebarRow"><span>Round trip</span><span class="sidebarValue">18 ms</span></div>
        </div>
      </dees-harness-sidebar>
    </div>
    ${demoOutput()}
  </div>
`);
